import type { ILender } from "@/types/companies-lenders";
import type { IContractingCompany } from "@/types/companies-contractors";
import { useBoolean } from "@/ui-kit";
import { RequestChangeCompanyForm } from "@/components/ChangeCompany";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CompanyDetailsCard } from "@/components/profile-cards";
import { CompanyChangeRepository } from "@/api/company-change/repository";
import { CompanyChangeService } from "@/api/company-change/service";
import {
  type ICompanyChangeRequest,
  RequestStatus,
  RequestType,
} from "@/types/company-change-request";
import { ChangeCompanyRequestAlert } from "./ChangeCompanyRequestAlert";

type IProps = {
  company: ILender | IContractingCompany | undefined;
  companyType: "lender" | "contractor";
};

export const CompanyDetails = ({ company, companyType }: IProps) => {
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [companyChangeRequest, setCompanyChangeRequest] = useState<
    ICompanyChangeRequest | undefined
  >(undefined);

  const requestsRepository = useMemo(() => {
    return new CompanyChangeRepository(new CompanyChangeService());
  }, []);

  useEffect(() => {
    if (!company) {
      return;
    }

    requestsRepository
      .getCurrentUserRequests()
      .then((response) => {
        const companyChangeRequest = response
          .filter((r) => {
            return (
              r.requestType === RequestType.ChangeCompany &&
              (r.status === RequestStatus.Pending ||
                r.status === RequestStatus.Rejected)
            );
          })
          .at(-1);
        setCompanyChangeRequest(companyChangeRequest);
      })
      .finally(() => {
        setRequestsLoading(false);
      });
  }, [requestsRepository, company]);

  const {
    value: showChangeCompanyModal,
    setTrue: openChangeCompanyModal,
    setFalse: dismissChangeCompanyModal,
  } = useBoolean(false);

  const onRequestCompanyChange = useCallback(
    (request: ICompanyChangeRequest) => {
      setCompanyChangeRequest(request);
    },
    [],
  );

  const canEdit = !(requestsLoading || companyChangeRequest);

  return (
    <>
      <CompanyDetailsCard
        company={company}
        onEdit={canEdit ? openChangeCompanyModal : undefined}
        banner={
          <ChangeCompanyRequestAlert
            request={companyChangeRequest}
            company={company}
            companyType={companyType}
          />
        }
      />
      {showChangeCompanyModal && (
        <RequestChangeCompanyForm
          companyType={companyType}
          onDismiss={dismissChangeCompanyModal}
          onSubmitSuccess={onRequestCompanyChange}
        />
      )}
    </>
  );
};
